import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createDocumentThunk,
  loadDocuments,
  loadTypes,
  markObsoleteThunk,
  setFilterStatus,
  setFilterTypeId,
  setPage,
  setSearch,
} from '../features/documents/documentsSlice';
import type { DocumentPayload, DocumentStatus, ControlledDocument } from '../features/documents/types';
import { fetchPendingAcknowledgements } from '../features/documents/documentsApi';
import type { PendingAcknowledgementItem } from '../features/documents/types';
import { AppDispatch, RootState } from '../store/store';
import { loadRoles } from '../features/roles/rolesSlice';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { showToast } from '../components/ui/toast.utils';
import { RequiredFieldsLegend } from '../components/RequiredFieldsLegend';

const STATUS_LABEL: Record<DocumentStatus, string> = {
  draft: 'Borrador',
  in_review: 'En revisión',
  approved: 'Aprobado',
  in_force: 'Vigente',
  obsolete: 'Obsoleto',
};

const STATUS_CLASS: Record<DocumentStatus, string> = {
  draft: 'bg-zinc-200 text-zinc-700',
  in_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  in_force: 'bg-green-100 text-green-800',
  obsolete: 'bg-red-100 text-red-800',
};

const docSchema = z.object({
  code: z.string().min(1, 'Requerido').max(64),
  title: z.string().min(1, 'Requerido').max(255),
  typeId: z.string().uuid('Seleccioná un tipo'),
  ownerArea: z.string().trim().max(160).optional(),
  notes: z.string().trim().max(5000).optional(),
});
type DocForm = z.infer<typeof docSchema>;

export function DocumentsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmDialog();
  const { canEdit } = usePlatformAccessLevel('documents.access');
  const { items, loading, pagination, search, filterTypeId, filterStatus, types } = useSelector(
    (s: RootState) => s.documents,
  );
  const { items: roles, loading: rolesLoading } = useSelector((s: RootState) => s.roles);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingAcks, setPendingAcks] = useState<PendingAcknowledgementItem[]>([]);

  const roleOptions = useMemo(() => roles.map((role) => role.name), [roles]);

  const reloadPendingAcks = useCallback(async () => {
    try {
      const items = await fetchPendingAcknowledgements();
      setPendingAcks(items);
    } catch {
      setPendingAcks([]);
    }
  }, []);

  useEffect(() => {
    void reloadPendingAcks();
  }, [reloadPendingAcks]);

  useEffect(() => {
    dispatch(loadTypes(true));
    dispatch(loadRoles({ pageSize: 200 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      loadDocuments({
        search,
        typeId: filterTypeId || undefined,
        status: filterStatus || undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
      }),
    );
  }, [dispatch, search, filterTypeId, filterStatus, pagination.page, pagination.pageSize]);

  const handleObsolete = useCallback(
    async (d: ControlledDocument) => {
      const ok = await confirm({
        title: 'Marcar como obsoleto',
        message: `¿Marcar "${d.code} — ${d.title}" como obsoleto?`,
        confirmText: 'Marcar obsoleto',
        variant: 'danger',
      });
      if (!ok) return;
      await dispatch(markObsoleteThunk(d.id)).unwrap();
    },
    [confirm, dispatch],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold">Control documental</h1>
        <div className="flex gap-2">
          <Link
            to="/documentos/tipos"
            className="rounded-md px-3 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
          >
            Tipos
          </Link>
          {canEdit && (
            <button
              onClick={() => setDialogOpen(true)}
              className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              + Nuevo documento
            </button>
          )}
        </div>
      </div>

      {pendingAcks.length > 0 && (
        <section className="border border-amber-200 bg-amber-50 rounded-md p-4 space-y-2">
          <h2 className="text-sm font-semibold text-amber-900">
            Documentos pendientes de acuse de lectura ({pendingAcks.length})
          </h2>
          <ul className="text-sm space-y-1">
            {pendingAcks.map((item) => (
              <li key={item.documentVersionId}>
                <Link
                  to={`/documentos/${item.documentId}`}
                  className="text-primary hover:underline"
                >
                  {item.documentCode} v{item.version} — {item.documentTitle}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por código, título, área…"
          className="border rounded-md px-3 py-2 flex-1 min-w-[240px]"
          value={search}
          onChange={(e) => dispatch(setSearch(e.target.value))}
        />
        <select
          className="border rounded-md px-3 py-2"
          value={filterTypeId}
          onChange={(e) => dispatch(setFilterTypeId(e.target.value))}
        >
          <option value="">Todos los tipos</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          className="border rounded-md px-3 py-2"
          value={filterStatus}
          onChange={(e) => dispatch(setFilterStatus(e.target.value as DocumentStatus | ''))}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Código</Th>
              <Th>Título</Th>
              <Th>Tipo</Th>
              <Th>Área</Th>
              <Th>Estado</Th>
              <Th>Versión vigente</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  No hay documentos
                </td>
              </tr>
            )}
            {!loading &&
              items.map((d) => (
                <tr key={d.id} className="border-t hover:bg-muted/40">
                  <td className="px-3 py-2 font-mono text-xs">{d.code}</td>
                  <td className="px-3 py-2">
                    <Link to={`/documentos/${d.id}`} className="text-primary hover:underline">
                      {d.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{d.type?.name ?? '—'}</td>
                  <td className="px-3 py-2">{d.ownerArea ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_CLASS[d.status]}`}>
                      {STATUS_LABEL[d.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{d.currentVersion?.version ?? '—'}</td>
                  <td className="px-3 py-2">
                    {canEdit && d.status !== 'obsolete' && (
                      <button
                        onClick={() => handleObsolete(d)}
                        className="text-xs rounded bg-destructive text-destructive-foreground px-2 py-1 hover:bg-destructive/90"
                      >
                        Marcar obsoleto
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Pager page={pagination.page} pages={pagination.pages} onChange={(p) => dispatch(setPage(p))} />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Nuevo documento</DialogTitle>
          </DialogHeader>
          <NewDocForm
            types={types}
            roleOptions={roleOptions}
            rolesLoading={rolesLoading}
            onCancel={() => setDialogOpen(false)}
            onCreated={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewDocForm({
  types,
  roleOptions,
  rolesLoading,
  onCancel,
  onCreated,
}: {
  types: { id: string; name: string }[];
  roleOptions: string[];
  rolesLoading: boolean;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DocForm>({
    resolver: zodResolver(docSchema),
    defaultValues: { code: '', title: '', typeId: '', ownerArea: '', notes: '' },
  });

  const submit = handleSubmit(async (values) => {
    const payload: DocumentPayload = {
      code: values.code,
      title: values.title,
      typeId: values.typeId,
    };
    if (values.ownerArea && values.ownerArea.length > 0) {
      payload.ownerArea = values.ownerArea;
    }
    if (values.notes && values.notes.length > 0) {
      payload.notes = values.notes;
    }
    try {
      await dispatch(createDocumentThunk(payload)).unwrap();
      onCreated();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'No se pudo crear el documento. Revisá los datos e intentá de nuevo.';
      showToast(message, 'error');
    }
  });

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Código *" error={errors.code?.message}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          placeholder="Ej: PRO-001"
          {...register('code')}
        />
      </Field>
      <Field label="Tipo *" error={errors.typeId?.message}>
        <select className="border rounded-md px-3 py-2 w-full" {...register('typeId')}>
          <option value="">Seleccionar tipo…</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Título *" error={errors.title?.message} colSpan={2}>
        <input
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          placeholder="Título del documento"
          {...register('title')}
        />
      </Field>
      <Field label="Área dueña" error={errors.ownerArea?.message} colSpan={2}>
        <select className="border rounded-md px-3 py-2 w-full" disabled={rolesLoading} {...register('ownerArea')}>
          <option value="">{rolesLoading ? 'Cargando roles…' : 'Seleccionar rol…'}</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Notas" error={errors.notes?.message} colSpan={2}>
        <textarea
          rows={3}
          className="border rounded-md px-3 py-2 w-full placeholder:text-muted-foreground"
          placeholder="Notas opcionales"
          {...register('notes')}
        />
      </Field>
      <RequiredFieldsLegend className="md:col-span-2" />
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md px-4 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Creando…' : 'Crear'}
        </button>
      </div>
    </form>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Field({
  label,
  children,
  error,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function Pager({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (p: number) => void;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex justify-end gap-2">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
      >
        Anterior
      </button>
      <span className="px-3 py-1 text-sm">
        Página {page} de {pages}
      </span>
      <button
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
      >
        Siguiente
      </button>
    </div>
  );
}
